import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import {
  createGatingConfiguration,
  updateGatingConfiguration, invalidateGatingConfigurations, deleteGatingConfiguration,
  createGatingConfigurationForMapping, fetchGatingConfigurationsForMapping
} from 'actions/gatingConfigurations';
import { updateAssetMapping } from 'actions/content';
import {
  updateLayoutManagerForContentGrid,
} from 'actions/contentGrid';
import { updateWidgetEditorState } from 'actions/editor';
import {
  arrangeLayoutContents, removeLayoutContents,
  fetchMappingsForLayoutManager, invalidateLayoutManagerMappings,
  updateLayoutContent, arrangeMappings
} from 'actions/layoutManager';

import {
  Container, Modal, Button, Form, Dropdown
} from 'react-bootstrap';

import _ from 'lodash';
import cx from 'classnames';
import LinesEllipsis from 'react-lines-ellipsis';
import { Sortable } from "@shopify/draggable";
import shortid from 'shortid';

import { EditorContext } from "components/Editor";
import { Footer, ModalHeader, getContentForFieldSelector, getFieldSelector } from '../components';
import ContentConfigCard from '../components/contentGatingCard';
import { ThumbnailControl } from '../components/thumbnailControl';
import StoryboardContext from "components/Storyboard/context";
import InfiniteScroll from "components/InfiniteScroll";
import { ContentCard } from '../components/contentCard';
import GatingConfigurations from 'components/gatings';
import { deleteAssetGating } from 'actions/gatingConfigurations';

import styles from './manualSection.module.scss'

const ARRANGE_BY_FILTERS = [
  {
    id: 'name-a-z',
    label: 'Ascending',
    sort: 'name',
    order: 'ASC'
  },
  {
    id: 'name-z-a',
    label: 'Descending',
    sort: 'name',
    order: 'DESC'
  },
  {
    id: 'date-latest-first',
    label: 'Added Date: Latest',
    sort: 'published_date',
    order: 'DESC'
  },
  {
    id: 'date-earliest-first',
    label: 'Added Date: Earliest',
    sort: 'published_date',
    order: 'ASC'
  }
]

const ManualSection = ({ className, widgetProps, onAddAssets, onClose }) => {
  const { id } = useParams();

  const dispatch = useDispatch()
  const [state, setState] = useState({
    selected: [],
    reArranged: false
  })

  const container = React.createRef(null);
  const sortable = useRef(null);
  const multiSelect = useRef(false);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const storyboardContext = React.useContext(StoryboardContext);

  const widgetId = _.get(widgetProps, 'uid', null);
  const layoutContent = _.get(widgetProps, 'content', null);
  const layoutId = _.get(layoutContent, 'id', null);
  const layoutManagerId = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.id`) || null);
  const contents = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.items`) || []);
  const loading = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.loading`, false));
  const arranging = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.arranging`, false));
  const pagination = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.pagination`, {}));
  const hasMoreItems = _.size(contents) > 0 &&  _.get(pagination, "current") < _.get(pagination, "totalPages") - 1;

  const deletedContents = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.deletedMappings`) || []);

  const updating = _.get(widgetProps, 'editorState.layout.updating', false);
  const updated = _.get(widgetProps, 'editorState.layout.updated', false);

  const selectedContents = (contents || []).filter(item => {
    var content = _.get(item, 'content.id', null);
    return _.includes(state.selected, content);
  });

  var gatingConfigs = useSelector((state) => _.get(state, `gatingConfigurations`));

  useEffect(() => {
    handleFetchMappings(0);

    if (!container.current) {
      return
    }

    sortable.current = new Sortable(container.current, {
      draggable: `.cs-content-card`,
      handle: `.cs-content-card .content-drag-icon`,
      delay: 300,
      mirror: {
        appendTo: `.${styles.scrollableContainer}`,
        constrainDimensions: true
      }
    });

    return () => {
      dispatch(invalidateLayoutManagerMappings());
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyListener);
    document.addEventListener('keyup', handleKeyListener);
    return () => {
      document.removeEventListener('keydown', handleKeyListener);
      document.addEventListener('keyup', handleKeyListener);

      dispatch(invalidateGatingConfigurations())
    }
  }, []);

  useEffect(() => {
    if (!container.current || !sortable.current) {
      return;
    }

    sortable.current.on("sortable:stop", handleMove);

    return () => {
      sortable.current.off('sortable:stop', handleMove);
    }

  }, [contents]);

  useEffect(() => {
    if (updated && !arranging) {
      handleClose()
    }
  }, [updated])

  useEffect(() => {
    if (pagination.current === 0 && !loading && contents.length > 0) {
      var firstAsset = _.head(contents)
      handleSelection({}, _.get(firstAsset, 'content.id'))
    }
  }, [loading])

  useEffect(() => {
    if (!arranging) {
      handleFetchMappings(0);
    }
  }, [arranging])

  const handleMove = (e) => {
    const oldIndex = e.data.oldIndex;
    const newIndex = e.data.newIndex;

    var mapping = contents[oldIndex];
    var content = _.get(mapping, 'content.id', null);

    dispatch(arrangeLayoutContents(editorSession, widgetId, content, newIndex));
  }

  const handleFetchMappings = (page) => {
    if (!layoutId) {
      return;
    }
    dispatch(fetchMappingsForLayoutManager(editorSession, widgetId, layoutId, page, 20));
  }

  const loadMore = () => {
    const nextPage = pagination.current + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleFetchMappings(nextPage);
  }

  const handleKeyListener = (e) => {
    multiSelect.current = (e.shiftKey || e.ctrlKey || e.metaKey);
  }

  const handleSelection = (e, id) => {
    var multiSelect = false;
    if (e.metaKey || e.shiftKey || e.ctrlKey) {
      multiSelect = true;
    }

    var selection = state.selected || [];

    var isSelected = selection.some(item => item === id);

    if (isSelected && !multiSelect) {
      return;
    }

    if (!isSelected) {
      const content = contents.find(item => item.content.id == id);
      let mappingType = content.entityType === "page" ? "page" : "asset";

      dispatch(fetchGatingConfigurationsForMapping(id, storyboardContext.id, mappingType));

      if (multiSelect) {
        selection = [...selection, id];
        selection = _.uniq(selection);
      } else {
        selection = [id];
      }
    }

    if (isSelected && multiSelect && selection.length > 1) {
      selection = selection.filter(item => item !== id);
    }

    setState(state => {
      return {
        ...state,
        selected: selection
      }
    });
  }

  const handleMappingChange = (mapping, change) => {
    dispatch(updateLayoutContent(editorSession, widgetProps.uid, mapping.content.id, change))
  }

  const handleSelectAll = (value) => {
    if (!value) {
      setState(state => {
        return {
          ...state,
          selected: []
        }
      })
      return
    }
    setState(state => {
      return {
        ...state,
        selected: (contents || []).map(item => _.get(item, 'content.id'))
      }
    })
  }

  const handleRemove = () => {
    const selectedContents = state.selected.map(selected => {
        return contents.find(item => item.content.id === selected);
    });

    const itemsDeleted = selectedContents.map(selectedContent => {
      return _.get(selectedContent, 'content.asset.id');
    });

    dispatch(removeLayoutContents(editorSession, widgetId, itemsDeleted));
    setState(state => {
      return {
        ...state,
        selected: []
      }
    })
  }

  const handleArrangeBy = (filter) => {
    setState(state => {
      return {
        ...state,
        reArranged: true
      }
    })
    dispatch(arrangeMappings(editorSession, widgetProps.uid, layoutId, id, filter.sort, filter.order));
  }

  const handleSave = () => {
    var mappingRequest = (contents || [])
      .map((item, index) => {
        return {
          ...item,
          content: _.get(item, 'content.id'),
          order: index,
          fields: (item.fields || []).map(field => {
            if (field.name === 'icon') {
              return {
                ...field,
                content: _.get(field, 'content.id')
              }
            } else if (_.get(field, 'content.type') === 'image') {
              return {
                ...field,
                content: _.get(field, 'content.id'),
                contentType: 'image'
              }
            } else if (_.get(field, 'content.type') === 'video') {
              return {
                ...field,
                content: _.get(field, 'content.id'),
                contentType: 'image'
              }
            }

            return field;
          })
        }
      });

    var layoutRequest = {
      ...layoutContent,
      mappings: mappingRequest,
      page: id,
      storyboard: storyboardContext.id,
      deletedMappings: deletedContents
    }

    dispatch(updateLayoutManagerForContentGrid(editorSession, widgetProps.uid, layoutId, layoutRequest));
  }

  const handleClose = () => {
    const existingLayout = _.get(widgetProps, 'editorState.layout') | {};
    const nextState = {
      deletedMappings: [],
      layout: {
        ...existingLayout,
        updated: state.reArranged
      }
    }

    dispatch(updateWidgetEditorState(editorSession, widgetId, nextState));
    onClose();
  }

  const ContentCardPlaceholder = () => {
    return (
      <div className={cx(styles.contentCard, styles.placeholder)}>
        <div className={cx(styles.iconWrapper)}></div>
        <div className={styles.detailsContainer}>
          <div className={styles.name}>
            <span>&nbsp;</span>
            <span>&nbsp;</span>
          </div>
          <div className={cx(styles.contentMetadata)}>
            {_.times(2).map(index => {
              return (
                <div key={index} className={styles.metadata}><span>&nbsp;</span></div>
              );
            })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <ModalHeader widgetProps={widgetProps} type="manual" onAddAssets={onAddAssets} onClose={handleClose} />
      <Modal.Body className={styles.modalBody}>
        <Container fluid className={cx(styles.manualContainer, className)}>
          <section className={cx(styles.assetGridLayout, {[styles.layoutChange]: _.size(selectedContents) > 1})}>
            <LayoutHeader
              handleSelectAll={handleSelectAll}
              handleRemove={handleRemove}
              onArrangeBy={handleArrangeBy}
            />
            <div className={styles.scrollableContainer}>
              <InfiniteScroll
                className={cx(styles.scrollable)}
                containerRef={container}
                loading={loading}
                hasMore={hasMoreItems}
                loadMore={loadMore}
              >
                {contents.map((data, index) => {
                  const selected = (state.selected || []).some(id => id === _.get(data, 'content.id', null));
                  const mappingId = _.get(data, "content.id", null);
                  const gatings = _.get(gatingConfigs, `${mappingId}.items`, []);
                  const isLocked = gatings.some(item => item.enabled === true);

                  return (
                    <ContentCard
                      key={_.get(data, 'content.id', null)}
                      selected={selected}
                      isLocked={isLocked}
                      onSelect={handleSelection}
                      assetData={data}
                    />
                  )
                })
                }
                {loading &&
                  _.times(_.size(contents) == 0 ? 4 : 7).map(index => {
                    return <ContentCardPlaceholder key={index} />
                  })
                }
              </InfiniteScroll>
            </div>
          </section>
          <section className={cx(styles.assetDetailLayout, {[styles.detailLayoutChange]: _.size(selectedContents) > 1})}>
            <EditMapping mappings={selectedContents} contents={contents} onChange={handleMappingChange} />
          </section>
        </Container>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Footer selected={_.size(state.selected)} updating={updating} onSave={handleSave} />
      </Modal.Footer>
    </>
  )
}

const LayoutHeader = ({ handleSelectAll, handleRemove, onArrangeBy }) => {
  const [selectAll, setSelectAll] = useState(false);

  const handleArrangeBy = (filter) => {
    onArrangeBy(filter);
  }

  const handleSelectAsset = (e) => {
    setSelectAll(!selectAll)
    handleSelectAll(e.target.checked)
  }

  return (
    <div className={styles.header}>
      <div className={styles.sorting}>
        <Form.Check
          type="checkbox"
          className={styles.selectAction}
          id='selection-check'
          onChange={handleSelectAsset}
        />
        <Button onClick={handleRemove} className={styles.deleteAction} variant="info">
          <i className={cx(styles.deleteIcon, 'cleverstory-trash')} />
        </Button>
        <Dropdown className={styles.arrangeDropdown}>
          <Dropdown.Toggle className={styles.selected}>
            <Button size='sm' variant='primary' className={styles.button}>
              Arrange By
              <i className={cx(styles.icon, 'cleverstory-drop-down')} />
            </Button>
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.arrangeAssetMenu}>
            {ARRANGE_BY_FILTERS.map((filter, index) => {
              return <Dropdown.Item key={index} onClick={handleArrangeBy.bind(this, filter)}>{filter.label}</Dropdown.Item>
            })}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  )
}


const EditMapping = ({ mappings, contents, onChange }) => {
  if (_.size(mappings) == 1) {
    const mapping = mappings[0];
    const entityType = _.get(mapping, 'type');
    return <EditAssetContent mapping={mapping} contents={contents} onChange={onChange} />
  }
  // else if (_.size(mappings) > 1) {
  //   const mapping = mappings[0];
  //   return <EditMultipleAssetContent mapping={mapping} onChange={onChange} />
  // }
  return false;
}

const EditAssetContent = ({ mapping, contents, onChange }) => {
  const [gatingSetting, setGatingSetting] = useState(false);

  var name = getContentForFieldSelector(mapping, 'name') || "";
  var icon = getContentForFieldSelector(mapping, 'icon');
  var description = getContentForFieldSelector(mapping, 'description') || "";
  const mappingId = _.get(mapping, "content.id", null);

  let originalContent = _.get(mapping, 'content.asset') || _.get(mapping, 'content');
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard"));
  const gatingConfigs = useSelector((state) => _.get(state, `gatingConfigurations.${mappingId}.items`, []) || []);
  const mapUpdating = useSelector((state) => _.get(state, "content.mapping.loading"));
  const creating = useSelector((state) => _.get(state, `gatingConfigurations.${mappingId}.creating`, false));
  const deletedGatingsId = useSelector((state) => _.get(state, `gatingConfigurations.deletedGating`, {}) || {});

  const dispatch = useDispatch();

  useEffect(() => {
    if(mapping) {
      const mappingType = _.get(mapping, "type", null);
      const mappingId = _.get(mapping, "content.id", null);
      
      const type = mappingType === "page" ? "page" : "asset";
      
      setGatingSetting(false);
      dispatch(fetchGatingConfigurationsForMapping(mappingId, storyboard.item.id, type));
    }
  }, [mapping]);

  const handleSaveGating = () => {
    const mappingType = _.get(mapping, "type", null);
    const mappingId = _.get(mapping, "content.id", null);

    let gatingConfigurations = gatingConfigs.filter((item) => item.updated === true).map(item => {
      return {
        ...item,
        gating: _.get(item, 'gating.id', null),
        target: {
          type: mappingType === "page" ? "page" : "asset",
          id: mappingType === "INTERACTIVE_CONTENT" ? mappingId : _.get(item, "target.id", null)
        }
      }
    })

    dispatch(createGatingConfigurationForMapping(storyboard.item.id, mappingId, gatingConfigurations))

    if (_.size(deletedGatingsId) > 0) {
      dispatch(deleteAssetGating(storyboard.item.id, deletedGatingsId))
    }
  }

  const handleFieldChange = (fieldName, value) => {
    const field = getFieldSelector(mapping, fieldName);
    if (!field) {
      return;
    }

    const request = {
      ...field,
      override: true,
      content: value
    }

    onChange(mapping, request);
  }

  const handleUpdateGating = (id, request) => {
    dispatch(updateGatingConfiguration(mappingId, id, request))
  }

  const handleDeleteGating = (id) => {
    dispatch(deleteGatingConfiguration(mappingId, id))
  }

  const removeHtmlTags = (htmlString) => {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    return doc.body.textContent || "";
  };

  return (
    <div className={styles.editMapping}>
      <GatingActionCard name={_.get(originalContent, 'name')} icon={_.get(originalContent, 'icon')} mapping={mapping} onChange={onChange} gatingSetting={gatingSetting} setGatingSetting={setGatingSetting} gatingConfigs={gatingConfigs} />
      {!gatingSetting && <div className={cx(styles.assetEditor, { [styles.showEditor]: !gatingSetting })}>
        <div className={styles.body}>
          <Form className={styles.form}>
            <Form.Group className={styles.field}>
              <ThumbnailControl
                value={icon}
                onChange={(image) => {
                  handleFieldChange('icon', image);
                }}
              />
            </Form.Group>
            <Form.Group className={styles.field}>
              <Form.Label className={styles.label}>
                Name
              </Form.Label>
              <Form.Control
                className={styles.control}
                name="name"
                value={name}
                onChange={(e) => {
                  handleFieldChange('name', e.target.value)
                }}
              />
            </Form.Group>
            <Form.Group className={styles.field}>
              <Form.Label className={styles.label}>
                Description
              </Form.Label>
              <Form.Control
                as="textarea" rows={4}
                className={styles.control}
                name="description"
                value={removeHtmlTags(description)}
                onChange={(e) => {
                  handleFieldChange('description', e.target.value)
                }}
              />
            </Form.Group>
          </Form>
        </div>
      </div>}
      {gatingSetting && <div className={cx(styles.gatingRegistration, { [styles.showEditor]: gatingSetting })}>
        {
          gatingConfigs.map((gatingConfig, index) => {
            return (
              <GatingConfigurations index={index}
                gatingLength={gatingConfigs.length}
                gatingConfig={gatingConfig}
                storyboard={storyboard}
                onUpdate={handleUpdateGating}
                onDelete={handleDeleteGating}
                type={"asset"} />
            )
          })
        }

      </div>}
      {gatingSetting &&
        <div className={cx(styles.saveGatingContainer)}>
          <Button size="sm" variant="primary" onClick={handleSaveGating}>
            {creating ? "Saving ..." : "Save Gating"}
          </Button>
        </div>}
    </div>
  );
}

const EditMultipleAssetContent = ({ mapping, onChange }) => {
  const dispatch = useDispatch();

  const [gatingSetting, setGatingSetting] = useState(false);
  var description = getContentForFieldSelector(mapping, 'description') || "";
  const mappingId = _.get(mapping, "content.id", null);
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard"));
  const gatingConfigs = useSelector((state) => _.get(state, `gatingConfigurations.${mappingId}.items`, []) || []);
  const mapUpdating = useSelector((state) => _.get(state, "content.mapping.loading"));

  const handleSaveGating = () => {
    const assetMappingId = _.get(mapping, "content.id", null);

    let gatingConfigurations = gatingConfigs.map(item => {
      return {
        ...item,
        gating: _.get(item, 'gating.id', null),
        target: {
          type: "asset",
          id: _.get(item, "target.id", null)
        }
      }
    })

    dispatch(updateAssetMapping(assetMappingId, { gatingConfigurations: gatingConfigurations }))
  }
  const handleFieldChange = (fieldName, value) => {
    const field = getFieldSelector(mapping, fieldName);
    if (!field) {
      return;
    }

    const request = {
      ...field,
      override: true,
      content: value
    }

    onChange(mapping, request);
  }

  const handleUpdateGating = (id, request) => {
    dispatch(updateGatingConfiguration(mappingId, id, request))
  }

  const handleDeleteGating = (id) => {
    dispatch(deleteGatingConfiguration(mappingId, id))
  }

  return (
    <div className={styles.editMapping}>
      <GatingActionCard mapping={mapping} onChange={onChange} gatingSetting={gatingSetting} setGatingSetting={setGatingSetting} gatingConfigs={gatingConfigs} storyboard={storyboard}/>
      {!gatingSetting && <div className={cx(styles.assetEditor, styles.showEditor)}>
        <div className={styles.body}>
          <Form className={styles.form}>
            <Form.Group className={styles.field}>
              <Form.Label className={styles.label}>
                Description
              </Form.Label>
              <Form.Control
                as="textarea" rows={4}
                className={styles.control}
                name="description"
                value={description}
                onChange={(e) => {
                  handleFieldChange('description', e.target.value)
                }}
              />
            </Form.Group>
            <Form.Group className={styles.field}>
              <Form.Label className={styles.label}>
                Special Tag
              </Form.Label>
              <Form.Control
                className={styles.control}
                name="tag"
                value=""
              />
            </Form.Group>
            <Form.Group className={styles.field}>
              <Form.Label className={styles.label}>
                CTA Text
              </Form.Label>
              <Form.Control
                className={styles.control}
                name="cta"
                value=""
              />
            </Form.Group>
          </Form>
        </div>
      </div>}
    </div>
  );
}

const GatingActionCard = ({ icon, name, mapping, gatingSetting, onChange, setGatingSetting, gatingConfigs }) => {
  const dispatch = useDispatch();
  const mappingType = _.get(mapping, "type", null);
  const mappingId = _.get(mapping, "content.id", null);
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard"));

  const createNewGating = () => {
    var request = {
      uid: shortid(),
      name: 'Untitled Gating',
      enabled: false,
      optional: false,
      target: {
        type: mappingType === "page" ? "page" : "asset",
        id: mappingId
      },
      trigger: { type: 'TIME', value: 10 }
    }

    dispatch(createGatingConfiguration(mappingId, request));
  }

  const handleFieldReset = (fieldName, value) => {
    const field = getFieldSelector(mapping, fieldName);
    if (!field) {
      return;
    }

    const request = {
      ...field,
      override: false,
      content: value
    }

    onChange(mapping, request);
  }

  const handleReset = () => {
    const name = _.get(mapping, 'content.asset.name');
    const description = _.get(mapping, 'content.asset.summary');
    var icon = _.get(mapping, 'content.asset.icon');

    if (icon) {
      const id = _.get(icon, 'id');
      const type = _.get(icon, 'type');
      const url = _.get(icon, 'key');

      icon = {
        id: id,
        type: type,
        url: url
      }
    }

    handleFieldReset("name", name);
    handleFieldReset("description", description);
    handleFieldReset("icon", icon);
  }

  return (
    <div className={cx(styles.wrapperContainer, { [styles.expandContainer]: gatingSetting })}>
      <div className={cx(styles.header, { [styles.showHeader]: gatingSetting }, { [styles.hideHeader]: !gatingSetting })}>
        <div className={styles.actions}>
          <span className={styles.backButton} onClick={() => setGatingSetting(false)}>
            <i className={cx('cleverstory-back')} /> Back
          </span>
          <Button className={styles.addButton} variant='secondary' onClick={createNewGating}>
            <i className={cx('cleverstory-plus')} />
            New Gating
          </Button>
        </div>
        <div className={styles.content}>
          <span className={styles.title}>
            Asset Gating
          </span>
          <span className={styles.subTitle}>
            Gating helps generate leads and personalize your marketing efforts.
          </span>
        </div>
      </div>
      <ContentConfigCard
        name={name}
        icon={icon}
        gatingSetting={gatingSetting}
        setGatingSetting={setGatingSetting}
        gatingConfigs={gatingConfigs}
        mapping={mapping}
        handleReset={handleReset}
      />
    </div>
  )
}

export default ManualSection